import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Plus,
  FolderOpen,
  Image,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
} from 'lucide-react';

type Project = {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  imageCount: number;
  completedCount: number;
  createdAt: number;
};

export default function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const result = await api.projects.list();
      setProjects(result.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateProject(e: React.FormEvent) {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setCreating(true);
    try {
      const result = await api.projects.create(newProjectName);
      setShowCreateDialog(false);
      setNewProjectName('');
      navigate(`/projects/${result.data.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteProject(id: string, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (!confirm('确定要删除这个项目吗？所有相关图片都会被删除。')) return;

    try {
      await api.projects.delete(id);
      setProjects(projects.filter((p) => p.id !== id));
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  }

  function getStatusBadge(status: string) {
    const config = {
      pending: { label: '待处理', color: 'bg-gray-100 text-gray-700', icon: Clock },
      processing: { label: '处理中', color: 'bg-blue-100 text-blue-700', icon: Clock },
      completed: { label: '已完成', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      failed: { label: '失败', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    };

    const { label, color, icon: Icon } = config[status as keyof typeof config] || config.pending;

    return (
      <Badge className={`${color} flex items-center space-x-1`}>
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </Badge>
    );
  }

  if (loading) {
    return <div className="text-center py-12">加载中...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">项目列表</h1>
          <p className="text-gray-500 mt-1">管理您的产品标注项目</p>
        </div>

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              新建项目
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>创建新项目</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="projectName">项目名称</Label>
                <Input
                  id="projectName"
                  placeholder="例如：可口可乐产品标注"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? '创建中...' : '创建'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">还没有项目</h3>
            <p className="text-gray-500 mb-4">创建您的第一个项目开始标注</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              新建项目
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link key={project.id} to={`/projects/${project.id}`}>
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg truncate">{project.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(project.createdAt).toLocaleDateString('zh-CN')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center">
                        <Image className="h-4 w-4 mr-1" />
                        图片数量
                      </span>
                      <span className="font-medium">{project.imageCount}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        已标注
                      </span>
                      <span className="font-medium">{project.completedCount}</span>
                    </div>

                    <div className="pt-2 border-t flex items-center justify-between">
                      <span className="text-sm text-gray-500">状态</span>
                      {getStatusBadge(project.status)}
                    </div>

                    {project.imageCount > 0 && (
                      <div className="pt-2">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>进度</span>
                          <span>
                            {project.imageCount > 0
                              ? Math.round((project.completedCount / project.imageCount) * 100)
                              : 0}
                            %
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{
                              width: `${
                                project.imageCount > 0
                                  ? (project.completedCount / project.imageCount) * 100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
